import * as React from "react";
import { Img, type ImgIntent } from "./Img.js";
import "./FieldArt.css";

/**
 * Field Day game-poster art.
 *
 * `ArtKind` + `artKindForSlug` remain so callers can keep assigning a stable
 * "kind" per slug, but the poster cover itself is no longer a stylised SVG:
 * `PosterArt` renders the real per-game thumbnail when present, an animated
 * shimmer skeleton while the catalog is loading, and a neutral static skeleton
 * for games with no thumbnail.
 */
export type ArtKind =
  | "space"
  | "dungeon"
  | "naval"
  | "fortress"
  | "pizza"
  | "ninja"
  | "skate"
  | "derby"
  | "forge"
  | "tycoon"
  | "quest"
  | "bakery";

const ART_KINDS: readonly ArtKind[] = [
  "space",
  "dungeon",
  "naval",
  "fortress",
  "pizza",
  "ninja",
  "skate",
  "derby",
  "forge",
  "tycoon",
  "quest",
  "bakery",
];

/**
 * Slug → cover-art kind. Add new mappings when new games ship. Any unmapped
 * slug rotates through the full set via `artKindForSlug`.
 */
export const ART_BY_SLUG: Record<string, ArtKind> = {
  "dungeon-of-digits": "dungeon",
  "fast-math-battleship": "naval",
  "fast-math-defense": "fortress",
  "math-crush": "bakery",
  "math-slice-dojo": "ninja",
  "smash-math-badminton": "skate",
  "sports-raiders": "quest",
};

/**
 * Pick an art kind for any slug. Tries the explicit mapping first; if the slug
 * is unknown, deterministically rotates through the full set so new games
 * still get distinct covers across the grid.
 */
export function artKindForSlug(slug: string, i = 0): ArtKind {
  const mapped = ART_BY_SLUG[slug];
  if (mapped) return mapped;
  // Hash the slug so the fallback is deterministic regardless of grid order.
  let h = i;
  for (let k = 0; k < slug.length; k++) h = (h * 31 + slug.charCodeAt(k)) | 0;
  return ART_KINDS[Math.abs(h) % ART_KINDS.length] as ArtKind;
}

/**
 * Poster cover renderer used by every Field Day game card.
 *
 * Render priority:
 * 1. `loading` → animated shimmer skeleton (catalog data still fetching).
 * 2. real per-game `thumbnail` → responsive <Img> (which itself shows a
 *    shimmer while the image decodes, then cross-fades in).
 * 3. otherwise → a neutral static skeleton placeholder.
 *
 * The image/skeleton fills the parent art container (`.v-field__poster-art`,
 * `.v-field__ring-art`, `.v-field-g__featured-art` — all position: relative).
 */
export function PosterArt({
  thumbnail,
  alt,
  priority = false,
  intent = "card",
  loading = false,
}: {
  thumbnail?: string | null;
  alt: string;
  /** Mark above-the-fold posters as priority for eager fetch + high priority. */
  priority?: boolean;
  /**
   * Rendering context for the responsive image. Most posters are grid cards
   * (`card`); large above-the-fold art (the catalog featured card, the game
   * detail hero) should pass `hero` so they pull a sharp high-res rung.
   */
  intent?: ImgIntent;
  /** When true, show an animated shimmer skeleton (data still loading). */
  loading?: boolean;
  /**
   * Stable art kind for the slug. No longer rendered (covers are thumbnails or
   * skeletons now) but accepted so existing call sites keep compiling.
   */
  kind?: ArtKind;
}): React.ReactElement | null {
  if (loading) {
    return <span className="poster-art-skeleton is-loading" aria-hidden="true" />;
  }
  if (thumbnail) {
    return (
      <Img
        src={thumbnail}
        alt={alt}
        width={400}
        height={400}
        priority={priority}
        intent={intent}
        style={{ position: "absolute", inset: 0 }}
      />
    );
  }
  return <span className="poster-art-skeleton" aria-hidden="true" />;
}
